import { processSigninResponse, signin } from "app/infrastructure/auth/token-manager";
import { AuthenticationFailed, ReceiveLogout, ReceiveToken } from "app/shell/action-types";
import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { debug, fatal } from "app/infrastructure/log";
import { globalVar } from "app/infrastructure/universal";
import { externalRedirect } from "app/infrastructure/platform";
import { AppSetting } from "app/infrastructure/hosting/app-settings";
import { logoutRoute } from "app/infrastructure/auth/constants";
import { toAbsoluteUrl } from "app/infrastructure/hosting";
import { AppDispatch } from "app/infrastructure/configure-store";
import { logout as performLogout } from "app/shell/logout";

export function useLogin() {
  const locationObj = globalVar('location');
  const [isSignedIn, setSignedIn] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string>(undefined);
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const isLogoutRoute = locationObj.pathname === toAbsoluteUrl(logoutRoute);
  const login = async () => await signin();
  const logout = performLogout;

  const getSigninResponse = useCallback(async () => {
    try {
      const url = await processSigninResponse(locationObj.href);
      debug('processToken', 'redirecting after login', url);
      setRedirectUrl(url);
    } catch (err) {
      fatal('Failed to process login response.', err);
      dispatch(new AuthenticationFailed());
    }
  }, []);

  useEffect(() => {
    if (!!redirectUrl) {
      dispatch(new ReceiveToken());
      setSignedIn(true);
      debug('processToken', 'redirecting after login', redirectUrl);
      navigate(redirectUrl, { replace: true });
      setRedirectUrl(undefined);
    }
  }, [dispatch, navigate, redirectUrl, debug, setRedirectUrl]);

  const processLogout = () => {
    try {
      const logoutLandingPage = AppSetting.LogoutLandingPage;

      if (!!logoutLandingPage) {
        externalRedirect(logoutLandingPage);
      } else {
        dispatch(new ReceiveLogout());
      }
    } catch (err) {
      fatal('Failed to process signout response.', err);
      dispatch(new AuthenticationFailed());
    }
  };

  return {
    isSignedIn,
    redirectUrl,
    getSigninResponse,
    login,
    logout,
    isLogoutRoute,
    processLogout
  };
}
